import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

interface FindingsAutoCollapse {
  controlsExpanded: boolean;
  toggleControls: () => void;
  findingsRef: RefObject<HTMLDivElement>;
}

/*
 * Shared panel behavior around the collapsible controls section: when findings
 * appear the controls collapse out of the way and the view scrolls to the
 * findings; when the review ends the controls re-expand. Manual toggles in
 * between are left alone — only the appear/clear transitions move the section.
 */
export function useFindingsAutoCollapse(hasFindings: boolean): FindingsAutoCollapse {
  const [controlsExpanded, setControlsExpanded] = useState(!hasFindings);
  const findingsRef = useRef<HTMLDivElement>(null);
  const wasFindings = useRef<boolean | null>(null);

  useEffect(() => {
    const appeared = hasFindings && wasFindings.current !== true;
    const cleared = !hasFindings && wasFindings.current === true;
    wasFindings.current = hasFindings;
    if (appeared) {
      setControlsExpanded(false);
      findingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (cleared) {
      setControlsExpanded(true);
    }
  }, [hasFindings]);

  const toggleControls = useCallback(() => setControlsExpanded((value) => !value), []);

  return { controlsExpanded, toggleControls, findingsRef };
}
